function waitForVideoMetadata(videoEl) {
  if (videoEl.readyState >= 1 && Number.isFinite(videoEl.duration)) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const onLoadedMetadata = () => {
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(new Error('Failed to load video metadata.'));
    };
    const cleanup = () => {
      videoEl.removeEventListener('loadedmetadata', onLoadedMetadata);
      videoEl.removeEventListener('error', onError);
    };

    videoEl.addEventListener('loadedmetadata', onLoadedMetadata, {once: true});
    videoEl.addEventListener('error', onError, {once: true});
  });
}

function seekAndWaitForFrame(videoEl, seekTime, {timeoutMs = 10_000} = {}) {
  return new Promise((resolve, reject) => {
    let settled = false;
    let timeoutId = null;

    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      videoEl.removeEventListener('seeked', onSeeked);
      videoEl.removeEventListener('loadeddata', onLoadedData);
      videoEl.removeEventListener('error', onError);
    };

    const finish = () => {
      if (settled) {
        return;
      }

      settled = true;
      cleanup();
      resolve();
    };

    const fail = (error) => {
      if (settled) {
        return;
      }

      settled = true;
      cleanup();
      reject(error);
    };

    const onError = () => fail(new Error('Video seek failed while capturing frame.'));
    const onLoadedData = () => {
      if (videoEl.readyState >= 2) {
        finish();
      }
    };

    const onSeeked = () => {
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(() => finish());
        return;
      }

      setTimeout(finish, 0);
    };

    videoEl.addEventListener('seeked', onSeeked, {once: true});
    videoEl.addEventListener('loadeddata', onLoadedData, {once: true});
    videoEl.addEventListener('error', onError, {once: true});

    timeoutId = setTimeout(() => fail(new Error('Timed out while seeking video frame.')), timeoutMs);

    videoEl.currentTime = seekTime;
  });
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function roundTimeBucket(seconds, precision = 100) {
  return Math.round(seconds * precision) / precision;
}

function normalizeDimension(value) {
  const nextValue = Number(value);
  return Number.isFinite(nextValue) && nextValue > 0 ? Math.round(nextValue) : null;
}

function resolveTargetDimensions({sourceWidth, sourceHeight, widthPx, heightPx, maxDimension}) {
  const safeSourceWidth = Math.max(1, Math.round(sourceWidth || 0));
  const safeSourceHeight = Math.max(1, Math.round(sourceHeight || 0));
  const safeMaxDimension = Number.isFinite(maxDimension) && maxDimension > 0
    ? Math.round(maxDimension)
    : Infinity;

  const explicitWidth = normalizeDimension(widthPx);
  const explicitHeight = normalizeDimension(heightPx);

  if (explicitWidth && explicitHeight) {
    return {
      width: explicitWidth,
      height: explicitHeight
    };
  }

  if (safeMaxDimension !== Infinity) {
    if (safeSourceWidth <= safeMaxDimension && safeSourceHeight <= safeMaxDimension) {
      return {
        width: safeSourceWidth,
        height: safeSourceHeight
      };
    }

    if (safeSourceWidth > safeSourceHeight) {
      const scale = safeMaxDimension / safeSourceWidth;
      return {
        width: safeMaxDimension,
        height: Math.max(1, Math.round(safeSourceHeight * scale))
      };
    }

    const scale = safeMaxDimension / safeSourceHeight;
    return {
      width: Math.max(1, Math.round(safeSourceWidth * scale)),
      height: safeMaxDimension
    };
  }

  if (explicitWidth) {
    return {
      width: explicitWidth,
      height: Math.max(1, Math.round(explicitWidth * (safeSourceHeight / safeSourceWidth)))
    };
  }

  if (explicitHeight) {
    return {
      width: Math.max(1, Math.round(explicitHeight * (safeSourceWidth / safeSourceHeight))),
      height: explicitHeight
    };
  }

  return {
    width: safeSourceWidth,
    height: safeSourceHeight
  };
}

export function useVideoFrameCapture() {
  const sourceAspectRatio = ref(16 / 9);

  let videoEl = null;
  let canvasEl = null;
  let canvasCtx = null;
  let mountedVideoEl = null;
  let activeSrc = null;
  let generationId = 0;
  let activeCaptureTask = null;
  const pendingCaptureTasks = [];
  const pendingCaptureTaskByKey = new Map();
  const cache = new Map();

  function ensureElements() {
    if (typeof document === 'undefined') {
      return;
    }

    if (!videoEl) {
      videoEl = document.createElement('video');
      videoEl.muted = true;
      videoEl.playsInline = true;
      videoEl.preload = 'metadata';
      videoEl.crossOrigin = 'anonymous';

      videoEl.style.position = 'fixed';
      videoEl.style.left = '-99999px';
      videoEl.style.top = '0';
      videoEl.style.width = '1px';
      videoEl.style.height = '1px';
      videoEl.style.opacity = '0';
      videoEl.style.pointerEvents = 'none';

      document.body.appendChild(videoEl);
      mountedVideoEl = videoEl;
    }

    if (!canvasEl) {
      canvasEl = document.createElement('canvas');
      canvasCtx = canvasEl.getContext('2d', {alpha: false});
    }
  }

  async function loadSource(src) {
    ensureElements();

    if (!videoEl || !src) {
      return null;
    }

    if (activeSrc === src && videoEl.currentSrc) {
      return {
        duration: Number(videoEl.duration) || 0,
        width: Number(videoEl.videoWidth) || 0,
        height: Number(videoEl.videoHeight) || 0,
        aspectRatio: sourceAspectRatio.value
      };
    }

    activeSrc = src;
    videoEl.src = src;
    videoEl.load();
    await waitForVideoMetadata(videoEl);

    if (videoEl.videoWidth && videoEl.videoHeight) {
      sourceAspectRatio.value = videoEl.videoWidth / videoEl.videoHeight;
    }

    return {
      duration: Number(videoEl.duration) || 0,
      width: Number(videoEl.videoWidth) || 0,
      height: Number(videoEl.videoHeight) || 0,
      aspectRatio: sourceAspectRatio.value
    };
  }

  function normalizeQueueKey(queueKey) {
    if (queueKey === null || queueKey === undefined) {
      return null;
    }

    return String(queueKey);
  }

  function settleCaptureTask(task, value = null) {
    if (!task || task.settled) {
      return;
    }

    task.settled = true;
    task.resolve(value);
  }

  function rejectCaptureTask(task, error) {
    if (!task || task.settled) {
      return;
    }

    task.settled = true;
    task.reject(error);
  }

  function dequeuePendingCaptureTask(task) {
    if (!task) {
      return;
    }

    const taskIndex = pendingCaptureTasks.indexOf(task);
    if (taskIndex !== -1) {
      pendingCaptureTasks.splice(taskIndex, 1);
    }

    if (task.queueKey && pendingCaptureTaskByKey.get(task.queueKey) === task) {
      pendingCaptureTaskByKey.delete(task.queueKey);
    }
  }

  async function runNextCaptureTask() {
    if (activeCaptureTask || !pendingCaptureTasks.length) {
      return;
    }

    const nextTask = pendingCaptureTasks.shift();
    if (!nextTask) {
      return;
    }

    if (nextTask.queueKey && pendingCaptureTaskByKey.get(nextTask.queueKey) === nextTask) {
      pendingCaptureTaskByKey.delete(nextTask.queueKey);
    }

    activeCaptureTask = nextTask;

    try {
      const result = await nextTask.run();
      settleCaptureTask(nextTask, result);
    } catch (error) {
      rejectCaptureTask(nextTask, error);
    } finally {
      if (activeCaptureTask === nextTask) {
        activeCaptureTask = null;
      }

      void runNextCaptureTask();
    }
  }

  function clearPendingCaptureTasks() {
    while (pendingCaptureTasks.length) {
      const pendingTask = pendingCaptureTasks.shift();
      if (pendingTask?.queueKey && pendingCaptureTaskByKey.get(pendingTask.queueKey) === pendingTask) {
        pendingCaptureTaskByKey.delete(pendingTask.queueKey);
      }

      settleCaptureTask(pendingTask, null);
    }

    pendingCaptureTaskByKey.clear();
  }

  function enqueueCapture(task, {queueKey = null, replacePending = false} = {}) {
    const normalizedQueueKey = normalizeQueueKey(queueKey);

    return new Promise((resolve, reject) => {
      const nextTask = {
        run: task,
        resolve,
        reject,
        queueKey: normalizedQueueKey,
        settled: false
      };

      if (replacePending && normalizedQueueKey) {
        const existingPendingTask = pendingCaptureTaskByKey.get(normalizedQueueKey);
        if (existingPendingTask) {
          dequeuePendingCaptureTask(existingPendingTask);
          settleCaptureTask(existingPendingTask, null);
        }
      }

      pendingCaptureTasks.push(nextTask);

      if (normalizedQueueKey) {
        pendingCaptureTaskByKey.set(normalizedQueueKey, nextTask);
      }

      void runNextCaptureTask();
    });
  }

  function captureCurrentFrame({
    widthPx,
    heightPx,
    maxDimension = Infinity,
    quality = 0.6,
    mimeType = 'image/jpeg'
  } = {}) {
    if (!videoEl || !canvasCtx) {
      return null;
    }

    const sourceWidth = Number(videoEl.videoWidth) || 0;
    const sourceHeight = Number(videoEl.videoHeight) || 0;
    if (!sourceWidth || !sourceHeight) {
      return null;
    }

    const {width, height} = resolveTargetDimensions({
      sourceWidth,
      sourceHeight,
      widthPx,
      heightPx,
      maxDimension
    });

    canvasEl.width = width;
    canvasEl.height = height;
    canvasCtx.drawImage(videoEl, 0, 0, width, height);

    try {
      return canvasEl.toDataURL(mimeType, quality);
    } catch {
      return null;
    }
  }

  function buildCacheKey({src, timeSec, widthPx, heightPx, maxDimension, quality, mimeType}) {
    return `${src}|${roundTimeBucket(timeSec)}|${Math.round(widthPx)}|${Math.round(heightPx)}|${maxDimension}|${quality}|${mimeType}`;
  }

  async function captureAtInternal({
    src,
    timeSec,
    widthPx,
    heightPx,
    maxDimension = Infinity,
    quality = 0.6,
    mimeType = 'image/jpeg',
    timeoutMs = 10_000
  } = {}, captureGeneration = generationId) {
    if (captureGeneration !== generationId) {
      return null;
    }

    const sourceMeta = await loadSource(src);

    if (!videoEl || !sourceMeta || captureGeneration !== generationId) {
      return null;
    }

    const resolvedDimensions = resolveTargetDimensions({
      sourceWidth: sourceMeta.width,
      sourceHeight: sourceMeta.height,
      widthPx,
      heightPx,
      maxDimension
    });

    const maxSeekTime = Math.max(0, (videoEl.duration || 0) - 0.001);
    const seekTime = clamp(Number(timeSec) || 0, 0, maxSeekTime);

    const cacheEntryKey = buildCacheKey({
      src,
      timeSec: seekTime,
      widthPx: resolvedDimensions.width,
      heightPx: resolvedDimensions.height,
      maxDimension: Number.isFinite(maxDimension) ? maxDimension : 'inf',
      quality,
      mimeType
    });

    if (cache.has(cacheEntryKey)) {
      return cache.get(cacheEntryKey);
    }

    if (Math.abs((videoEl.currentTime || 0) - seekTime) < 0.001 && videoEl.readyState >= 2) {
      const currentFrame = captureCurrentFrame({
        widthPx: resolvedDimensions.width,
        heightPx: resolvedDimensions.height,
        maxDimension,
        quality,
        mimeType
      });

      if (currentFrame) {
        cache.set(cacheEntryKey, currentFrame);
      }

      return currentFrame;
    }

    // If the browser is still at metadata-only state on the initial frame, nudge the seek.
    if (Math.abs((videoEl.currentTime || 0) - seekTime) < 0.001 && videoEl.readyState < 2) {
      await seekAndWaitForFrame(videoEl, clamp(seekTime + 0.001, 0, maxSeekTime), {timeoutMs});
    } else {
      await seekAndWaitForFrame(videoEl, seekTime, {timeoutMs});
    }

    if (captureGeneration !== generationId) {
      return null;
    }

    if (Math.abs((videoEl.currentTime || 0) - seekTime) > 0.001) {
      await seekAndWaitForFrame(videoEl, seekTime, {timeoutMs});
    }

    if (captureGeneration !== generationId) {
      return null;
    }

    const imageSrc = captureCurrentFrame({
      widthPx: resolvedDimensions.width,
      heightPx: resolvedDimensions.height,
      maxDimension,
      quality,
      mimeType
    });

    if (imageSrc) {
      cache.set(cacheEntryKey, imageSrc);
    }

    return imageSrc;
  }

  function captureAt(options = {}) {
    const {
      queueKey = null,
      replacePending = false,
      ...captureOptions
    } = options;
    const captureGeneration = generationId;
    return enqueueCapture(
      () => captureAtInternal(captureOptions, captureGeneration),
      {queueKey, replacePending}
    );
  }

  function captureBatch({
    src,
    captures = [],
    widthPx,
    heightPx,
    maxDimension = Infinity,
    quality = 0.6,
    mimeType = 'image/jpeg',
    timeoutMs = 10_000,
    queueKey = null,
    replacePending = false
  } = {}) {
    const batchGeneration = generationId;
    return enqueueCapture(async () => {
      const results = [];

      for (const capture of captures) {
        if (batchGeneration !== generationId) {
          break;
        }

        const nextTime = typeof capture === 'number'
          ? capture
          : capture?.timeSec;

        const imageSrc = await captureAtInternal({
          src,
          timeSec: nextTime,
          widthPx: capture?.widthPx ?? widthPx,
          heightPx: capture?.heightPx ?? heightPx,
          maxDimension: capture?.maxDimension ?? maxDimension,
          quality: capture?.quality ?? quality,
          mimeType: capture?.mimeType ?? mimeType,
          timeoutMs
        }, batchGeneration);

        results.push({
          timeSec: Number(nextTime) || 0,
          imageSrc
        });
      }

      return results;
    }, {queueKey, replacePending});
  }

  function clearCache() {
    cache.clear();
  }

  function cancel() {
    generationId++;
    clearPendingCaptureTasks();
  }

  function dispose() {
    cancel();

    if (mountedVideoEl?.parentNode) {
      mountedVideoEl.parentNode.removeChild(mountedVideoEl);
    }

    mountedVideoEl = null;
    videoEl = null;
    canvasEl = null;
    canvasCtx = null;
    activeSrc = null;
    activeCaptureTask = null;
    clearPendingCaptureTasks();
    sourceAspectRatio.value = 16 / 9;
    clearCache();
  }

  return {
    sourceAspectRatio,
    loadSource,
    captureAt,
    captureBatch,
    captureCurrentFrame,
    clearCache,
    cancel,
    dispose
  };
}
